import useSWR, { useSWRConfig } from 'swr';
import useApplication, { getCurrentAppOpts } from '../../unified/useApplication';
import { orderBy } from 'lodash';
import { CustomerData, LoanDetails, Offer, rawOffersProps } from 'merchant-api/types';
import { offerOrchestrator } from '..';
import { queryOptions } from '@tanstack/react-query';
import { AppError, queryClient } from 'merchant-api'
import { useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export const selectedOfferIdAtom = atomWithStorage<string | null>('checkoutSelectedOfferId', null);

export interface UseDetailedOffersProps {
  isLoadingDetailedOffers: boolean;
  offers: Offer[];
  tlOffers: Offer[];
  selectedOffer?: Offer;
  previousLoan?: LoanDetails;
  customerData: CustomerData;
  isErrorOffers: any;
  remainingPrincipal?: number;
  experienceType?: 'configurable' | string;
  response: any;
  mutate: any;
  isExpired: boolean;
  partnerAccountName?: string;
  partnerAccountDba?: string;
}

export function useDetailedOffers(): UseDetailedOffersProps {
  const apiPath = import.meta.env['VITE_OFFER_ORCHESTRATOR_URL'];
  const {
    applicationData,
    isLoadingApplication,
    isErrorApplication,
  } = useApplication(['APPROVED']);

  const { fetcher: globalFetcher } = useSWRConfig();
  const selectedOfferId = useAtomValue(selectedOfferIdAtom);

  const swrKey = applicationData ? `${apiPath}/v2/detailedOffers?applicationId=${applicationData.id}` : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    (url) => globalFetcher(`${url}${selectedOfferId ? `&offerId=${selectedOfferId}` : ''}`),
    {revalidateOnMount: true, revalidateOnFocus: false}
  );

  const previousLoan = data?.loanDetails;
  const customerData = data?.customerData;

  const filterProductOffer = (product: string, offers: Offer[]) =>
    offers
      ?.filter(
        (offer) =>
          offer?.product === product && offer?.presentationStatus === 'ACTIVE',
      )
      .map((offer) =>
        offer.pricedDetails.paymentFrequency === 'WEEKLY'
          ? { ...offer, pricedDetails: {...offer.pricedDetails, paymentFrequency: 'Weekly' }}
          : offer,
      )
      .map((offer) =>
        offer.pricedDetails.paymentFrequency === 'DAILY'
          ? { ...offer, pricedDetails: {...offer.pricedDetails,  paymentFrequency: 'Daily' }}
          : offer,
      );
  const tlArray = filterProductOffer(
    'TermLoan',
    data?.applicationOffer?.offers,
  );
  const filteredTLArray = tlArray?.filter(
    (offer) => !offer.prePaymentDiscountRelatedOfferIds?.hasOwnProperty('100'),
  );
  const orderedTLOffers = orderBy(
    filteredTLArray,
    [(o) => o.prePaymentDiscountPct === 100, 'pricedDetails.lineAmount', 'pricedDetails.term'],
    ['desc', 'desc', 'desc'],
  );

  const selectedOffer = data
    ? data?.applicationOffer?.offers.filter(
        (o) => o.offerId === data?.applicationOffer.selectedOffer,
      )[0] || null
    : undefined;

  const expirationDate = data?.applicationOffer?.expirationDate ? new Date(data.applicationOffer.expirationDate) : false;
  const isExpired = expirationDate && expirationDate < new Date();

  return {
    isLoadingDetailedOffers: isLoading || isLoadingApplication,
    remainingPrincipal: previousLoan?.remainingPrincipal,
    offers: data?.applicationOffer?.offers,
    experienceType: data?.applicationOffer?.experienceType,
    tlOffers: orderedTLOffers,
    selectedOffer: selectedOffer,
    previousLoan: previousLoan,
    customerData: customerData!,
    isErrorOffers: error || isErrorApplication,
    response: data,
    mutate: mutate,
    isExpired: isExpired,
    partnerAccountName: data?.partnerAccountName,
    partnerAccountDba: data?.partnerAccountDba,
  };
}

export interface detailedOfferProps {
  data: rawOffersProps;
  TLoffers: Offer[];
  LOCoffers: Offer[];
  isExpired: boolean | false;
  hasOffers?: boolean;
}

const getDetailedOffers = async (applicationId?: string): Promise<detailedOfferProps> => {
  if (!applicationId) {
    const  data = await queryClient.fetchQuery(getCurrentAppOpts(['APPROVED']));
    applicationId = data?.id;
  }

 return offerOrchestrator.get<rawOffersProps>("/v2/detailedOffers", { params: { applicationId }, validateStatus: (status) => [200, 404].includes(status) })
.then(res => {
  if (res.status === 404) return {} as rawOffersProps;
  return res.data
})
.then(data => {
  const TLoffers = data?.applicationOffer?.offers?.filter((offer) =>
          offer.product === 'TermLoan',
      )
  const LOCoffers = data?.applicationOffer?.offers?.filter((offer) =>
      offer.product === 'LineOfCredit',
  )
  const expirationDate = data?.applicationOffer?.expirationDate ? new Date(data.applicationOffer.expirationDate) : false;
  const isExpired = expirationDate && expirationDate < new Date()
  const hasOffers = (TLoffers?.length || 0) + (LOCoffers?.length || 0) > 0;
  const isNew = !data?.loanDetails && !isExpired;

  if(data?.applicationOffer?.selectedOffer === null && (LOCoffers?.length > 0 || data?.applicationOffer?.experienceType?.toLowerCase() === 'fixed' || isNew)) throw AppError.NewUser;
  return { data, TLoffers, LOCoffers, isExpired, hasOffers };
  });
}

export const getDetailedOffersOpts = (applicationId?: string) => queryOptions<detailedOfferProps>({
  queryKey: ['detailedOffers'],
  queryFn: () => getDetailedOffers(applicationId),
  refetchOnWindowFocus: false
});

export default useDetailedOffers;
